import {
	createIntakeDraftStore,
	type IntakeDraftStore,
} from '#app/routes/mocra+/_form+/intake/create-intake-draft-store.ts'
import { type IntakeDraftEnvelope } from '#app/routes/mocra+/_form+/intake/intake-draft.ts'

const STORAGE_KEY = 'mocra-intake'

let storageStatus: 'unknown' | 'available' | 'unavailable' = 'unknown'
let draftStore: IntakeDraftStore | null = null

function getByteSize(serializedValue: string): number {
	if (typeof TextEncoder !== 'undefined') {
		return new TextEncoder().encode(serializedValue).length
	}
	return serializedValue.length
}

function getLocalStorageStats() {
	if (typeof window === 'undefined') return null
	try {
		let totalBytes = 0
		const entries: Record<
			string,
			{
				sizeBytes: number
				sizeKilobytes: number
			}
		> = {}
		for (let index = 0; index < window.localStorage.length; index += 1) {
			const key = window.localStorage.key(index)
			if (!key) continue
			const rawValue = window.localStorage.getItem(key) ?? ''
			const valueBytes = getByteSize(rawValue)
			totalBytes += valueBytes
			entries[key] = {
				sizeBytes: valueBytes,
				sizeKilobytes: Number((valueBytes / 1024).toFixed(2)),
			}
		}
		return {
			entryCount: Object.keys(entries).length,
			totalBytes,
			totalKilobytes: Number((totalBytes / 1024).toFixed(2)),
			entries,
		}
	} catch {
		return null
	}
}

function markStorageUnavailable(error: unknown) {
	if (storageStatus === 'unavailable') return
	storageStatus = 'unavailable'
	if (error) {
		console.warn('Local storage unavailable for MoCRA intake drafts', error)
	}
}

function markStorageAvailable() {
	storageStatus = 'available'
}

function canUseLocalStorage(): boolean {
	if (typeof window === 'undefined') return false
	if (storageStatus === 'available') return true
	if (storageStatus === 'unavailable') return false
	try {
		const probeKey = `${STORAGE_KEY}__probe`
		window.localStorage.setItem(probeKey, '1')
		window.localStorage.removeItem(probeKey)
		markStorageAvailable()
		return true
	} catch (error) {
		markStorageUnavailable(error)
		return false
	}
}

function loadFromLocalStorage(): IntakeDraftEnvelope | null {
	if (!canUseLocalStorage()) return null
	try {
		const raw = window.localStorage.getItem(STORAGE_KEY)
		if (!raw) return null
		const parsed = JSON.parse(raw) as Record<string, unknown> | null
		if (!parsed || typeof parsed !== 'object') return null
		if (
			'sections' in parsed &&
			typeof (parsed as { sections?: unknown }).sections === 'object' &&
			parsed.sections !== null
		) {
			const version =
				typeof (parsed as { version?: unknown }).version === 'number'
					? (parsed as { version: number }).version
					: 0
			return {
				version,
				sections: parsed.sections as Record<string, unknown>,
			}
		}
		return {
			version: 0,
			sections: parsed,
		}
	} catch (error) {
		markStorageUnavailable(error)
		throw error
	}
}

function saveToLocalStorage(envelope: IntakeDraftEnvelope) {
	if (!canUseLocalStorage()) return
	const serializedEnvelope = JSON.stringify(envelope)
	try {
		window.localStorage.setItem(STORAGE_KEY, serializedEnvelope)
		markStorageAvailable()
	} catch (error) {
		const totalBytes = getByteSize(serializedEnvelope)
		const sections = Object.keys(envelope.sections)
		const sectionSizes = Object.fromEntries(
			sections.map((key) => {
				try {
					const value = envelope.sections[key]
					const serializedValue = JSON.stringify(value)
					return [key, serializedValue ? getByteSize(serializedValue) : 0]
				} catch {
					return [key, null]
				}
			}),
		)
		console.info('Attempting to persist MoCRA intake draft', {
			totalBytes,
			totalKilobytes: Number((totalBytes / 1024).toFixed(2)),
			sectionCount: sections.length,
			sections,
			sectionSizes,
			draft: envelope.sections,
			localStorageStats: getLocalStorageStats(),
		})
		markStorageUnavailable(error)
		throw error
	}
}

function clearFromLocalStorage() {
	if (!canUseLocalStorage()) return
	try {
		window.localStorage.removeItem(STORAGE_KEY)
		markStorageAvailable()
	} catch (error) {
		markStorageUnavailable(error)
		throw error
	}
}

function ensureDraftStore(): IntakeDraftStore {
	if (draftStore) return draftStore
	if (typeof window === 'undefined') {
		draftStore = createIntakeDraftStore()
		return draftStore
	}
	draftStore = createIntakeDraftStore({
		persistence: {
			load: loadFromLocalStorage,
			save: saveToLocalStorage,
			clear: clearFromLocalStorage,
		},
		onPersistError: (error) => {
			markStorageUnavailable(error)
		},
		onPersistSuccess: () => {
			markStorageAvailable()
		},
	})
	return draftStore
}

export function loadSection<T>(section: string): T | null {
	if (typeof window === 'undefined') return null
	const store = ensureDraftStore()
	const storedValue = store.getState().sections[section] as T | undefined
	return storedValue ?? null
}

export function saveSection<T>(section: string, value: T) {
	if (typeof window === 'undefined') return
	const store = ensureDraftStore()
	store.updateSection(section, value)
}

export function clearSection(section: string) {
	if (typeof window === 'undefined') return
	const store = ensureDraftStore()
	store.clearSection(section)
}

export function clearAllSections() {
	if (typeof window === 'undefined') return
	const store = ensureDraftStore()
	store.clearAll()
}

export function getIntakeDraftStore(): IntakeDraftStore {
	return ensureDraftStore()
}
